<template>
  <div
    class="dash-shell"
    :class="{ inspect: inspectMode, locked: locked }"
    :style="shellStyle"
  >
    <!-- 设计稿：顶栏通栏 -->
    <header
      class="dash-header"
      :class="{ 'dash-header--branch': !!$route.query.branchId }"
      :style="{ '--hdr-ornament': 'url(' + headerOrnament + ')' }"
    >
      <div class="dash-header__left">
        <img class="logo-img" :src="displayLogo" alt="logo" />
        <span class="org-name">{{ leftOrgName }}</span>
      </div>
      <div class="dash-header__center">
        <h1 class="header-title">{{ headerTitle }}</h1>
      </div>
      <div class="dash-header__right">
        <template v-if="!locked">
          <template v-if="$route.query.branchId">
            <span v-if="home.branchName" class="branch-chip">
              <i class="ri-git-branch-line" aria-hidden="true" />
              当前分会：{{ home.branchName }}
            </span>
            <button type="button" class="btn btn-branch-back" @click="clearBranch">
              返回全区排名
            </button>
          </template>
          <button
            v-if="inspectMode"
            type="button"
            class="btn btn-exit-inspect"
            :class="{ pressing: inspectPressing }"
            :style="{ '--press-progress': inspectPressProgress + '%' }"
            title="长按 3 秒后输入管理密码退出迎检"
            @mousedown.prevent="onInspectPress"
            @mouseup="onInspectRelease"
            @mouseleave="onInspectRelease"
            @touchstart.prevent="onInspectPress"
            @touchend="onInspectRelease"
            @touchcancel="onInspectRelease"
          >
            <span class="btn-exit-inspect__fill" aria-hidden="true" />
            <span class="btn-exit-inspect__label">
              {{ inspectPressing ? "继续按住…" : "退出迎检" }}
            </span>
          </button>
          <button v-else type="button" class="btn btn-danger" @click="enterInspect">
            一键迎检
          </button>
          <button
            type="button"
            class="btn btn-mic"
            :class="{
              'is-on': voiceOn,
              'is-flash': voiceFlash
            }"
            :title="voiceOn ? '语音已开启（再点关闭）' : '点击开启语音指令'"
            :aria-pressed="voiceOn ? 'true' : 'false'"
            @click="toggleVoice"
          >
            <i :class="voiceOn ? 'ri-mic-fill' : 'ri-mic-off-line'" aria-hidden="true" />
          </button>
          <button type="button" class="btn" @click="setLocked(true)">锁屏</button>
          <button type="button" class="btn" @click="onShot">截图</button>
        </template>
        <button
          v-else
          type="button"
          class="btn btn-unlock"
          title="输入管理密码解锁"
          @click="openUnlockDialog"
        >
          解锁
        </button>
        <span class="clock">{{ clock }}</span>
      </div>
      <div class="dash-header__ornament" aria-hidden="true" />
    </header>

    <div class="dash-mid">
      <aside v-if="!locked" class="dash-side">
        <button
          v-for="p in pages"
          :key="p.path"
          type="button"
          class="dash-side__item"
          :class="{ active: isActive(p.path) }"
          @click="go(p.path)"
        >
          <i
            :class="isActive(p.path) ? p.iconActive || p.icon : p.icon"
            class="dash-side__icon"
            aria-hidden="true"
          />
          <span class="dash-side__label">{{ p.short }}</span>
        </button>
      </aside>

      <div class="dash-body">
        <main class="dash-main" ref="shotRoot">
          <router-view
            :rem="rem"
            :common-font="litterFS"
            :home="home"
            :inspect-mode="inspectMode"
          />
        </main>
        <!-- 锁屏：遮住内容交互，仍可见首页画面 -->
        <div v-if="locked" class="lock-block" aria-hidden="true" />
      </div>
    </div>

    <!-- 解锁密码（仅点「解锁」后出现） -->
    <div
      v-if="locked && lockPwdVisible"
      class="lock-mask"
      @mousedown.self="closeUnlockDialog"
    >
      <div class="lock-box" @mousedown="bumpLockPwdIdle" @keydown="bumpLockPwdIdle">
        <div class="lock-box__glow" aria-hidden="true" />
        <div class="lock-box__icon" aria-hidden="true">
          <i class="ri-lock-unlock-fill" />
        </div>
        <h3 class="lock-box__title">解锁大屏</h3>
        <p class="lock-box__hint">请输入管理密码恢复操作</p>
        <div class="lock-box__field">
          <el-input
            ref="lockPwdInput"
            v-model="lockPwd"
            show-password
            placeholder="请输入管理密码"
            @input="bumpLockPwdIdle"
            @keyup.enter.native="unlock"
          />
        </div>
        <button type="button" class="lock-box__btn" @click="unlock">确认解锁</button>
      </div>
    </div>

    <!-- 一键迎检：全屏欢迎页（盖住首页，点任意处关闭） -->
    <div
      v-if="welcomeVisible"
      class="welcome-mask"
      role="dialog"
      aria-label="迎检欢迎"
      @click="dismissWelcome"
    >
      <p class="welcome-mask__text">{{ welcomeText }}</p>
    </div>

    <!-- 退出迎检 -->
    <div v-if="pwdVisible" class="lock-mask" @mousedown.self="pwdVisible = false">
      <div class="lock-box">
        <div class="lock-box__glow" aria-hidden="true" />
        <div class="lock-box__icon" aria-hidden="true">
          <i class="ri-shield-keyhole-fill" />
        </div>
        <h3 class="lock-box__title">退出迎检</h3>
        <p class="lock-box__hint">请输入管理密码退回日常管理视图</p>
        <div class="lock-box__field">
          <el-input
            ref="exitPwdInput"
            v-model="exitPwd"
            show-password
            placeholder="请输入管理密码"
            @keyup.enter.native="confirmExitInspect"
          />
        </div>
        <button type="button" class="lock-box__btn" @click="confirmExitInspect">确认退出</button>
      </div>
    </div>

    <!-- 语音识别成功：底部浅提示 1 秒 -->
    <transition name="voice-tip">
      <div v-if="voiceTip" class="voice-tip" role="status">{{ voiceTip }}</div>
    </transition>

    <el-drawer
      :title="drawerTitle"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="42%"
      append-to-body
      custom-class="dash-drawer"
      @closed="onDrawerClosed"
    >
      <div
        ref="drawerScroll"
        class="drawer-scroll"
        @scroll.passive="onDrawerScroll"
      >
        <div v-if="drawerSearchable" class="drawer-search">
          <el-input
            v-model="drawerKeyword"
            clearable
            size="small"
            placeholder="搜索姓名 / 单位 / 职务"
            @keyup.enter.native="onDrawerSearch"
            @clear="onDrawerSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="onDrawerSearch" />
          </el-input>
        </div>
        <ul
          v-if="drawerSummary.length && drawerListLayout !== 'points' && drawerListLayout !== 'active' && drawerListLayout !== 'bucket' && drawerListLayout !== 'response' && drawerListLayout !== 'participate' && drawerListLayout !== 'order-status' && drawerListLayout !== 'flow-hero'"
          class="drawer-summary"
          :class="{
            'drawer-summary--sandan': drawerSummaryLayout === 'sandan',
            'drawer-summary--rows': drawerSummaryLayout === 'rows' || drawerSummaryLayout === 'sandan',
            'drawer-summary--objects': drawerSummaryIsObjects
          }"
        >
          <li
            v-for="(s, i) in drawerSummary"
            :key="'s' + i"
            :class="{
              'is-accent': s && s.accent,
              'is-wide': s && s.wide
            }"
          >
            <template v-if="s && typeof s === 'object' && s.label != null">
              <em class="drawer-summary__label">{{ s.label }}</em>
              <strong class="drawer-summary__value">{{ s.value }}</strong>
            </template>
            <template v-else>{{ s }}</template>
          </li>
        </ul>
        <div
          v-if="drawerListLayout === 'order-status'"
          class="drawer-order-status"
        >
          <div class="drawer-order-status__hero">
            <span class="drawer-order-status__hero-glow" aria-hidden="true" />
            <span class="drawer-order-status__hero-icon">
              <i class="ri-file-list-3-fill" aria-hidden="true" />
            </span>
            <div class="drawer-order-status__hero-body">
              <em>{{
                drawerSummary[0] && drawerSummary[0].label
                  ? drawerSummary[0].label
                  : "总点单数"
              }}</em>
              <strong>{{
                drawerSummary[0] && drawerSummary[0].value != null
                  ? drawerSummary[0].value
                  : "—"
              }}</strong>
            </div>
          </div>
          <h4 class="drawer-order-status__heading">状态分布</h4>
          <div v-if="drawerList.length" class="drawer-order-status__chart">
            <div
              v-for="(it, i) in drawerList"
              :key="it.name || i"
              class="drawer-order-status__col"
            >
              <span class="drawer-order-status__val">{{
                it.value != null ? it.value : "—"
              }}</span>
              <span
                class="drawer-order-status__bar"
                :class="'tone-' + orderStatusTone(it.name)"
                :style="{ height: orderStatusBarHeight(it) }"
              />
              <span class="drawer-order-status__month">{{ it.name }}</span>
            </div>
          </div>
          <ul v-if="drawerList.length" class="drawer-order-status__list">
            <li v-for="(it, i) in drawerList" :key="'os' + (it.name || i)">
              <strong>{{ it.name }}</strong>
              <span class="drawer-order-status__meta">{{ it.value }} 单</span>
              <span
                class="drawer-order-status__share"
                :class="'tone-' + orderStatusTone(it.name)"
              >{{ orderStatusShare(it) }}</span>
            </li>
          </ul>
          <p v-else class="drawer-order-status__empty">暂无点单状态数据</p>
        </div>
        <div
          v-else-if="drawerListLayout === 'flow-hero'"
          class="drawer-order-status"
        >
          <div class="drawer-order-status__hero">
            <span class="drawer-order-status__hero-glow" aria-hidden="true" />
            <span class="drawer-order-status__hero-icon">
              <i
                :class="drawerHeroIcon || 'ri-send-plane-fill'"
                aria-hidden="true"
              />
            </span>
            <div class="drawer-order-status__hero-body">
              <em>{{
                drawerSummary[0] && drawerSummary[0].label
                  ? drawerSummary[0].label
                  : "核心指标"
              }}</em>
              <strong>{{
                drawerSummary[0] && drawerSummary[0].value != null
                  ? drawerSummary[0].value
                  : "—"
              }}</strong>
            </div>
          </div>
          <p
            v-if="drawerSummary[1] && drawerSummary[1].label"
            class="drawer-order-status__sub"
          >
            {{ drawerSummary[1].label }}
            <b>{{ drawerSummary[1].value }}</b>
          </p>
        </div>

        <div
          v-else-if="drawerListLayout === 'qingdaoshi'"
          class="drawer-order-status"
        >
          <div class="drawer-order-status__hero">
            <span class="drawer-order-status__hero-glow" aria-hidden="true" />
            <span class="drawer-order-status__hero-icon">
              <i
                :class="drawerHeroIcon || 'ri-send-plane-fill'"
                aria-hidden="true"
              />
            </span>
            <div class="drawer-order-status__hero-body">
                <em>{{
                drawerSummary[0] && drawerSummary[0].label
                  ? drawerSummary[0].label
                  : ""
              }}</em>
              <strong>{{
                drawerSummary[0] && drawerSummary[0].value != null
                  ? drawerSummary[0].value
                  : ""
              }}</strong>
            </div>
          </div>
          
        </div>

        <div v-if="drawerItems.length" class="drawer-rich">
          <template v-for="(it, i) in drawerItems">
            <h4
              v-if="it._section"
              :key="'sec-' + i"
              class="drawer-section__title"
            >
              {{ it.title }}
            </h4>
            <article
              v-else
              :key="'card-' + i"
              class="drawer-card"
            >
            <div
              v-if="it.coverUrl && !(it.thumbs && it.thumbs.length)"
              class="drawer-card__cover"
              :style="{ backgroundImage: 'url(' + absUrl(it.coverUrl) + ')' }"
            >
              <span class="drawer-card__cover-shade" />
            </div>
            <div class="drawer-card__main">
              <div
                v-if="it.category || it.time || it.status || it.author || (it.statusTags && it.statusTags.length) || it.highlight || it.rating > 0"
                class="drawer-card__tags"
              >
                <span v-if="it.category" class="tag tag--cat">{{ it.category }}</span>
                <span
                  v-if="it.status"
                  class="tag"
                  :class="statusTagClass(it.status)"
                >{{ it.status }}</span>
                <span
                  v-for="(tag, ti) in (it.statusTags || [])"
                  :key="'st-' + ti"
                  class="tag"
                  :class="statusTagClass(tag)"
                >{{ tag }}</span>
                <span
                  v-if="it.highlight"
                  class="tag tag--hl"
                >★ {{ it.highlight }}</span>
                <span
                  v-if="it.rating > 0"
                  class="drawer-card__stars"
                  :aria-label="it.rating + '星'"
                >{{ "★".repeat(it.rating) }}{{ "☆".repeat(5 - it.rating) }}</span>
                <span v-if="it.time" class="tag tag--time">{{ it.time }}</span>
                <span v-if="it.author" class="tag">{{ it.author }}</span>
              </div>
              <p v-else-if="it.meta" class="drawer-card__meta">{{ it.meta }}</p>
              <h4 v-if="it.title" class="drawer-card__title">{{ it.title }}</h4>
              <div
                v-if="it.thumbs && it.thumbs.length && !it.thumbsAfter"
                class="drawer-thumbs"
              >
                <el-image
                  v-for="(t, ti) in it.thumbs"
                  :key="ti"
                  class="drawer-thumbs__item"
                  :src="absUrl(t.coverUrl || t.url || t)"
                  :preview-src-list="thumbPreviewList(it.thumbs)"
                  fit="cover"
                />
              </div>
              <div
                v-if="it.content"
                class="drawer-card__body"
                :class="{ 'is-collapsed': isBodyCollapsed(i, it.content) }"
              >{{ it.content }}</div>
              <button
                v-if="needBodyToggle(it.content)"
                type="button"
                class="drawer-card__more"
                @click="toggleBody(i)"
              >
                {{ isBodyCollapsed(i, it.content) ? "展开全文" : "收起" }}
              </button>
              <div
                v-if="it.thumbs && it.thumbs.length && it.thumbsAfter"
                class="drawer-thumbs drawer-thumbs--after"
              >
                <p class="drawer-thumbs__label">批示 / 证明材料</p>
                <el-image
                  v-for="(t, ti) in it.thumbs"
                  :key="'a' + ti"
                  class="drawer-thumbs__item"
                  :src="absUrl(t.coverUrl || t.url || t)"
                  :preview-src-list="thumbPreviewList(it.thumbs)"
                  fit="cover"
                />
              </div>
            </div>
            </article>
          </template>
        </div>
        <div
          v-else-if="drawerListLayout === 'response'"
          class="drawer-response"
        >
          <div class="drawer-response__hero">
            <span class="drawer-response__hero-glow" aria-hidden="true" />
            <span class="drawer-response__hero-icon">
              <i class="ri-timer-flash-fill" aria-hidden="true" />
            </span>
            <div class="drawer-response__hero-body">
              <em>{{
                drawerSummary[0] && drawerSummary[0].label
                  ? drawerSummary[0].label
                  : "当前平均响应"
              }}</em>
              <strong>{{
                drawerSummary[0] && drawerSummary[0].value != null
                  ? drawerSummary[0].value
                  : "—"
              }}</strong>
            </div>
          </div>
          <p
            v-if="responseVerdict"
            class="drawer-response__verdict"
            :class="'is-' + responseVerdict.tone"
          >
            {{ responseVerdict.text }}
          </p>
          <h4 class="drawer-response__heading">近 6 个月趋势</h4>
          <div v-if="drawerList.length" class="drawer-response__chart">
            <div
              v-for="(it, i) in drawerList"
              :key="it.month || i"
              class="drawer-response__col"
            >
              <span class="drawer-response__val">{{
                it.hours != null ? it.hours : "—"
              }}</span>
              <span
                class="drawer-response__bar"
                :style="{ height: responseBarHeight(it) }"
                :class="{ 'is-empty': it.hours == null }"
              />
              <span class="drawer-response__month">{{ it.name || it.month }}</span>
            </div>
          </div>
          <ul v-if="drawerList.length" class="drawer-response__list">
            <li v-for="(it, i) in drawerList" :key="'r' + (it.month || i)">
              <strong>{{ it.name || it.month }}</strong>
              <span class="drawer-response__meta">
                <template v-if="it.hours != null">
                  {{ it.hours }} 小时
                  <em v-if="it.samples != null">· {{ it.samples }} 单</em>
                </template>
                <template v-else>暂无数据</template>
              </span>
              <span
                v-if="responseDelta(it, i)"
                class="drawer-response__delta"
                :class="'is-' + responseDelta(it, i).tone"
              >{{ responseDelta(it, i).text }}</span>
            </li>
          </ul>
          <p v-else class="drawer-response__empty">暂无响应时长趋势数据</p>
        </div>
        <div
          v-else-if="drawerListLayout === 'participate'"
          class="drawer-participate"
        >
          <div class="drawer-participate__hero">
            <span class="drawer-participate__hero-glow" aria-hidden="true" />
            <span class="drawer-participate__hero-icon">
              <i class="ri-pie-chart-2-fill" aria-hidden="true" />
            </span>
            <div class="drawer-participate__hero-body">
              <em>{{
                drawerSummary[0] && drawerSummary[0].label
                  ? drawerSummary[0].label
                  : "当月参与率"
              }}</em>
              <strong>{{
                drawerSummary[0] && drawerSummary[0].value != null
                  ? drawerSummary[0].value
                  : "—"
              }}</strong>
            </div>
          </div>
          <p
            v-if="participateVerdict"
            class="drawer-participate__verdict"
            :class="'is-' + participateVerdict.tone"
          >
            {{ participateVerdict.text }}
          </p>
          <h4 class="drawer-participate__heading">近 6 个月趋势 · 同比去年</h4>
          <div v-if="drawerList.length" class="drawer-participate__chart">
            <div
              v-for="(it, i) in drawerList"
              :key="it.month || i"
              class="drawer-participate__col"
            >
              <span class="drawer-participate__val">{{
                it.rate != null ? it.rate + "%" : "—"
              }}</span>
              <div class="drawer-participate__bars">
                <span
                  class="drawer-participate__bar drawer-participate__bar--now"
                  :style="{ height: participateBarHeight(it.rate) }"
                  :title="'本年 ' + (it.rate != null ? it.rate + '%' : '—')"
                />
                <span
                  class="drawer-participate__bar drawer-participate__bar--ly"
                  :style="{ height: participateBarHeight(it.lastYearRate) }"
                  :title="'去年 ' + (it.lastYearRate != null ? it.lastYearRate + '%' : '—')"
                />
              </div>
              <span class="drawer-participate__month">{{ it.name || it.month }}</span>
            </div>
          </div>
          <div v-if="drawerList.length" class="drawer-participate__legend">
            <span><i class="is-now" />本年</span>
            <span><i class="is-ly" />去年同期</span>
          </div>
          <ul v-if="drawerList.length" class="drawer-participate__list">
            <li v-for="(it, i) in drawerList" :key="'p' + (it.month || i)">
              <strong>{{ it.name || it.month }}</strong>
              <span class="drawer-participate__meta">
                {{ it.rate != null ? it.rate + "%" : "—" }}
                <em v-if="it.active != null">· {{ it.active }} 人</em>
                <em class="drawer-participate__ly">
                  · 去年 {{ it.lastYearRate != null ? it.lastYearRate + "%" : "—" }}
                </em>
              </span>
              <span
                v-if="participateYoy(it)"
                class="drawer-participate__delta"
                :class="'is-' + participateYoy(it).tone"
              >{{ participateYoy(it).text }}</span>
            </li>
          </ul>
          <p v-else class="drawer-participate__empty">暂无参与率趋势数据</p>
        </div>
        <div
          v-else-if="drawerListLayout === 'active'"
          class="drawer-active"
        >
          <div class="drawer-active__hero">
            <span class="drawer-active__hero-glow" aria-hidden="true" />
            <span class="drawer-active__hero-icon">
              <i class="ri-user-star-fill" aria-hidden="true" />
            </span>
            <div class="drawer-active__hero-body">
              <em>{{
                drawerSummary[0] && drawerSummary[0].label
                  ? drawerSummary[0].label
                  : "当月活跃"
              }}</em>
              <strong>{{
                drawerSummary[0] && drawerSummary[0].value != null
                  ? drawerSummary[0].value
                  : activeListCount + " 人"
              }}</strong>
            </div>
          </div>
          <p class="drawer-active__rule">
            当月至少参与 1 次会议培训 / 学习教育 / 公益服务 / 三单接单 / 品牌活动
          </p>
          <h4 class="drawer-active__heading">活跃会员列表</h4>
          <ul v-if="drawerList.length" class="drawer-active__list">
            <li v-for="(it, i) in drawerList" :key="it.memberId || i">
              <span
                class="drawer-active__avatar"
                :style="memberAvatarBg(it)"
              >{{ memberAvatarUrl(it) ? "" : ((it.name || "?").trim().slice(0, 1)) }}</span>
              <div class="drawer-active__meta">
                <strong>{{ it.name || "会员" }}</strong>
                <em v-if="it.unit">{{ it.unit }}</em>
                <div v-if="it.sources && it.sources.length" class="drawer-active__tags">
                  <span
                    v-for="(src, si) in it.sources"
                    :key="si"
                    class="drawer-active__tag"
                    :class="'tag-' + sourceTone(src)"
                  >{{ src }}</span>
                </div>
              </div>
              <span class="drawer-active__idx">{{ i + 1 }}</span>
            </li>
          </ul>
          <p v-else class="drawer-active__empty">本月暂无活跃会员</p>
        </div>
        <div
          v-else-if="drawerListLayout === 'bucket'"
          class="drawer-bucket"
        >
          <div class="drawer-bucket__hero">
            <span class="drawer-bucket__hero-glow" aria-hidden="true" />
            <span class="drawer-bucket__hero-icon">
              <i
                :class="drawerHeroIcon || 'ri-bar-chart-horizontal-fill'"
                aria-hidden="true"
              />
            </span>
            <div class="drawer-bucket__hero-body">
              <em>{{
                drawerSummary[0] && drawerSummary[0].label
                  ? drawerSummary[0].label
                  : "该积分段"
              }}</em>
              <strong>{{
                drawerSummary[0] && drawerSummary[0].value != null
                  ? drawerSummary[0].value
                  : "—"
              }}</strong>
            </div>
          </div>
          <p
            v-if="drawerSummary[1] && drawerSummary[1].value"
            class="drawer-bucket__range"
          >
            积分区间
            <b>{{ drawerSummary[1].value }}</b>
            <span v-if="drawerSummary[1].label"> · {{ drawerSummary[1].label }}</span>
          </p>
          <h4 class="drawer-bucket__heading">
            {{ drawerListHeading || "会员列表" }}
          </h4>
          <ul v-if="drawerList.length" class="drawer-bucket__list">
            <li v-for="(it, i) in drawerList" :key="it.memberId || i">
              <span
                class="drawer-bucket__avatar"
                :style="memberAvatarBg(it)"
              >{{ memberAvatarUrl(it) ? "" : ((it.name || "?").trim().slice(0, 1)) }}</span>
              <div class="drawer-bucket__meta">
                <strong>{{ it.name || "会员" }}</strong>
                <em v-if="it.unit">{{ it.unit }}</em>
              </div>
              <span class="drawer-bucket__points">
                {{ it.points != null ? it.points : "—" }}<small>分</small>
              </span>
            </li>
          </ul>
          <p v-else class="drawer-bucket__empty">该分段暂无会员</p>
        </div>
        <div
          v-else-if="drawerList.length && drawerListLayout === 'points'"
          class="drawer-points"
        >
          <div v-if="drawerSummary.length" class="drawer-points__hero">
            <span class="drawer-points__hero-glow" aria-hidden="true" />
            <span class="drawer-points__hero-icon">
              <i class="ri-medal-fill" aria-hidden="true" />
            </span>
            <div class="drawer-points__hero-body">
              <em>{{
                drawerSummary[0] && drawerSummary[0].label
                  ? drawerSummary[0].label
                  : "会员总积分"
              }}</em>
              <strong>{{
                drawerSummary[0] && drawerSummary[0].value != null
                  ? drawerSummary[0].value
                  : drawerSummary[0]
              }}</strong>
            </div>
          </div>
          <h4 class="drawer-points__heading">{{ drawerListHeading || "积分获取方式" }}</h4>
          <ul class="drawer-points__rules">
            <li
              v-for="(it, i) in drawerList"
              :key="i"
              :class="'tone-' + (it.tone != null ? it.tone : i % 5)"
            >
              <span
                class="drawer-points__icon"
                :style="it.color ? { '--rule-color': it.color } : null"
              >
                <i :class="it.icon || 'ri-star-fill'" aria-hidden="true" />
              </span>
              <span class="drawer-points__name">{{ it.title || it }}</span>
              <span v-if="it.points != null" class="drawer-points__badge">
                {{ Number(it.points) > 0 ? "+" : "" }}{{ it.points }}<small>分</small>
              </span>
            </li>
          </ul>
        </div>
        <ul v-else-if="drawerList.length" class="drawer-list">
          <li v-for="(it, i) in drawerList" :key="i">{{ it }}</li>
        </ul>
        <p
          v-if="
            !drawerItems.length &&
              !drawerList.length &&
              !drawerSummary.length &&
              !drawerLoading
          "
          class="drawer-empty"
        >
          暂无内容
        </p>
        <p v-if="drawerLoading" class="drawer-tip">加载中…</p>
        <p
          v-else-if="drawerInfinite && drawerHasMore === false && drawerItems.length"
          class="drawer-tip"
        >
          已加载全部
        </p>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { Message } from "element-ui";
import html2canvas from "html2canvas";
import "remixicon/fonts/remixicon.css";
import defaultLogo from "@/assets/dash/logo.svg";
import headerOrnament from "@/assets/dash/header-center.png";
import { absMediaUrl, toDetailItem, memberAvatarUrl, memberAvatarStyle } from "@/utils/mediaUrl";
import { isSameJson } from "@/utils/stableData";
import { createVoiceCommander } from "@/utils/voiceCommand";

const PAGES = [
  {
    path: "/overview",
    short: "总览",
    icon: "ri-home-4-line",
    iconActive: "ri-home-4-fill"
  },
  {
    path: "/thought",
    short: "思想引领",
    icon: "ri-cpu-line",
    iconActive: "ri-cpu-fill"
  },
  {
    path: "/org",
    short: "组织建设",
    icon: "ri-group-line",
    iconActive: "ri-group-fill"
  },
  {
    path: "/duty",
    short: "履职建言",
    icon: "ri-briefcase-line",
    iconActive: "ri-briefcase-fill"
  },
  {
    path: "/activity",
    short: "活动公益",
    icon: "ri-hand-heart-line",
    iconActive: "ri-hand-heart-fill"
  },
  {
    path: "/mechanismNew",
    short: "机制创新",
    icon: "ri-settings-3-line",
    iconActive: "ri-settings-3-fill"
  },
  {
    path: "/branch",
    short: "分会",
    icon: "ri-organization-chart",
    iconActive: "ri-organization-chart"
  }
];
const CACHE_KEY_PREFIX = "xl_dash_home_cache";
/** 旧版单 key，仅作全区回退兼容 */
const CACHE_KEY_LEGACY = "xl_dash_home_cache";
const LOCK_KEY = "xl_dash_locked";
const INSPECT_KEY = "xl_dash_inspect";
/** 迎检轮播：用户操作后暂停，无操作满此时长再自动续播 */
const CAROUSEL_RESUME_MS = 60 * 1000;
/** 解锁密码框：无操作自动收起 */
const LOCK_PWD_IDLE_MS = 10 * 1000;

export default {
  name: "DashShell",
  props: {
    rem: { type: Number, required: true }
  },
  data() {
    return {
      pages: PAGES,
      headerOrnament,
      clock: "",
      clockTimer: null,
      refreshTimer: null,
      idleTimer: null,
      carouselTimer: null,
      carouselResumeTimer: null,
      home: {},
      inspectMode: false,
      welcomeVisible: false,
      locked: false,
      lockPwd: "",
      lockPwdVisible: false,
      lockPwdIdleTimer: null,
      exitPwd: "",
      pwdVisible: false,
      pressTimer: null,
      pressRaf: null,
      pressStartedAt: 0,
      inspectPressing: false,
      inspectPressProgress: 0,
      pageIndex: 0,
      drawerVisible: false,
      drawerTitle: "",
      drawerList: [],
      drawerItems: [],
      drawerSummary: [],
      drawerSummaryLayout: "",
      drawerListLayout: "",
      drawerInfinite: null,
      drawerSkip: 0,
      drawerHasMore: false,
      drawerLoading: false,
      drawerBodyExpanded: {},
      drawerSearchable: false,
      drawerKeyword: "",
      drawerHeroIcon: "",
      drawerListHeading: "",
      voiceOn: false,
      voiceFlash: false,
      voiceTip: "",
      voiceTipTimer: null,
      voiceFlashTimer: null,
      voiceCommander: null
    };
  },
  computed: {
    litterFS() {
      return 12 * this.rem;
    },
    /** 迎检轮播页列表；分会模式下跳过「分会」全区排名页，避免清掉 branchId */
    carouselPages() {
      const inBranch = !!(this.$route.query && this.$route.query.branchId);
      if (!inBranch) return this.pages;
      return this.pages.filter(p => p.path !== "/branch");
    },
    drawerSummaryIsObjects() {
      const list = this.drawerSummary || [];
      return (
        list.length > 0 &&
        list.every(s => s && typeof s === "object" && s.label != null)
      );
    },
    activeListCount() {
      return (this.drawerList || []).length;
    },
    responseBarMax() {
      const vals = (this.drawerList || [])
        .map(it => Number(it && it.hours))
        .filter(v => Number.isFinite(v) && v > 0);
      return vals.length ? Math.max(...vals) : 1;
    },
    orderStatusBarMax() {
      const vals = (this.drawerList || [])
        .map(it => Number(it && it.value))
        .filter(v => Number.isFinite(v) && v > 0);
      return vals.length ? Math.max(...vals) : 1;
    },
    orderStatusTotal() {
      return (this.drawerList || []).reduce((s, it) => {
        const n = Number(it && it.value);
        return s + (Number.isFinite(n) ? n : 0);
      }, 0);
    },
    responseVerdict() {
      const list = (this.drawerList || []).filter(
        it => it && it.hours != null && Number.isFinite(Number(it.hours))
      );
      if (list.length < 2) return null;
      const first = Number(list[0].hours);
      const last = Number(list[list.length - 1].hours);
      const diff = Math.round((last - first) * 10) / 10;
      if (Math.abs(diff) < 0.05) {
        return { tone: "flat", text: "近几个月响应时长基本持平" };
      }
      if (diff < 0) {
        return {
          tone: "up",
          text: `较期初加快 ${Math.abs(diff)} 小时，整体趋向更快`
        };
      }
      return {
        tone: "down",
        text: `较期初慢了 ${diff} 小时，需关注响应时效`
      };
    },
    participateBarMax() {
      const vals = [];
      (this.drawerList || []).forEach(it => {
        if (!it) return;
        const a = Number(it.rate);
        const b = Number(it.lastYearRate);
        if (Number.isFinite(a) && a > 0) vals.push(a);
        if (Number.isFinite(b) && b > 0) vals.push(b);
      });
      return vals.length ? Math.max(...vals) : 1;
    },
    participateVerdict() {
      const list = (this.drawerList || []).filter(
        it => it && it.yoy != null && Number.isFinite(Number(it.yoy))
      );
      if (!list.length) return null;
      const avg =
        list.reduce((s, it) => s + Number(it.yoy), 0) / list.length;
      const diff = Math.round(avg);
      if (Math.abs(diff) < 1) {
        return { tone: "flat", text: "近半年参与率与去年同期基本持平" };
      }
      if (diff > 0) {
        return {
          tone: "up",
          text: `近半年参与率同比平均提升约 ${diff} 个百分点`
        };
      }
      return {
        tone: "down",
        text: `近半年参与率同比平均下降约 ${Math.abs(diff)} 个百分点`
      };
    },
    org() {
      return (this.home && this.home.org) || {};
    },
    orgName() {
      // 顶栏左侧：协会全称（后台「协会全称」）
      return this.org.orgName || "市北区新的社会阶层人士联谊会";
    },
    leftOrgName() {
      return this.orgName;
    },
    titlePrefix() {
      return (
        this.org.titlePrefix ||
        "市北区新的社会阶层人士实践创新基地数据中心"
      );
    },
    headerTitle() {
      // 顶栏正中：后台「大屏中间标题」，不再与左侧协会名拼接
      return this.titlePrefix;
    },
    displayLogo() {
      const url = this.org.logoUrl;
      return url ? this.absUrl(url) : defaultLogo;
    },
    shellStyle() {
      const url = this.org.backgroundUrl;
      if (!url) return {};
      return {
        backgroundImage: `url(${this.absUrl(url)})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      };
    },
    welcomeText() {
      return this.org.welcomeText || "欢迎视察";
    }
  },
  watch: {
    "$route.query.branchId"() {
      // 先切到该分会（或全区）自己的缓存，避免短暂显示上一视角数据
      this.restoreCache();
      this.loadHome();
    },
    "$route.path"(val) {
      const i = this.pages.findIndex(p => p.path === val);
      if (i >= 0) this.pageIndex = i;
      this.syncHomeRefreshTimer();
      if (val === "/overview") this.loadHome();
      if (this.locked && val !== "/overview") {
        this.$router.replace({ path: "/overview", query: {} });
      }
    },
    lockPwdVisible(val) {
      if (val) {
        this.bumpLockPwdIdle();
        this.$nextTick(() => {
          const input = this.$refs.lockPwdInput;
          if (input && input.focus) input.focus();
        });
      } else {
        this.clearLockPwdIdle();
      }
    },
    pwdVisible(val) {
      if (val) {
        this.pauseCarouselForInteraction();
        this.$nextTick(() => {
          const input = this.$refs.exitPwdInput;
          if (input && input.focus) input.focus();
        });
      } else if (this.inspectMode) {
        this.scheduleCarouselResume();
      }
    },
    drawerVisible(val) {
      if (val) this.pauseCarouselForInteraction();
      else if (this.inspectMode) this.scheduleCarouselResume();
    },
    locked(val) {
      if (val) this.pauseCarouselForInteraction();
      else if (this.inspectMode) this.scheduleCarouselResume();
    },
    welcomeVisible(val) {
      if (val) this.pauseCarouselForInteraction();
      else if (this.inspectMode) this.scheduleCarouselResume();
    }
  },
  created() {
    this.restoreLock();
    this.restoreInspect();
    this.restoreCache();
    this.tick();
    this.clockTimer = setInterval(this.tick, 1000);
    // 顶栏 org 需要至少一次 home；10s 轮询仅停在总览时开启
    this.loadHome();
    this.syncHomeRefreshTimer();
    this.bindIdle();
    this.$root.$on("dash-drawer", this.openDrawer);
    this.voiceCommander = createVoiceCommander({
      onCommand: this.onVoiceCommand,
      onStateChange: ({ enabled }) => {
        this.voiceOn = !!enabled;
      },
      onError: reason => {
        this.showVoiceSetupWarning(reason);
      }
    });
  },
  beforeDestroy() {
    clearInterval(this.clockTimer);
    this.stopHomeRefreshTimer();
    clearInterval(this.carouselTimer);
    clearTimeout(this.carouselResumeTimer);
    clearTimeout(this.idleTimer);
    clearTimeout(this.voiceTipTimer);
    clearTimeout(this.voiceFlashTimer);
    clearTimeout(this.lockPwdIdleTimer);
    if (this.voiceCommander) {
      this.voiceCommander.destroy();
      this.voiceCommander = null;
    }
    this.onInspectRelease();
    window.removeEventListener("pointerdown", this.resetIdle);
    this.$root.$off("dash-drawer", this.openDrawer);
  },
  methods: {
    absUrl(url) {
      return absMediaUrl(url);
    },
    memberAvatarUrl,
    memberAvatarBg(it) {
      return memberAvatarStyle(it);
    },
    sourceTone(src) {
      const s = String(src || "");
      if (s.indexOf("学习") >= 0) return "learn";
      if (s.indexOf("会议") >= 0) return "meet";
      if (s.indexOf("公益") >= 0) return "vol";
      if (s.indexOf("三单") >= 0) return "sandan";
      if (s.indexOf("品牌") >= 0) return "brand";
      return "default";
    },
    responseBarHeight(it) {
      if (!it || it.hours == null || !Number.isFinite(Number(it.hours))) {
        return "8px";
      }
      const h = Number(it.hours);
      const max = this.responseBarMax || 1;
      const px = Math.max(10, Math.round((h / max) * 96));
      return `${px}px`;
    },
    orderStatusBarHeight(it) {
      const v = Number(it && it.value);
      if (!Number.isFinite(v) || v <= 0) return "8px";
      const max = this.orderStatusBarMax || 1;
      const px = Math.max(10, Math.round((v / max) * 96));
      return `${px}px`;
    },
    orderStatusTone(name) {
      const t = String(name || "");
      if (t.indexOf("评") >= 0) return "gold";
      if (t.indexOf("接") >= 0) return "green";
      if (t.indexOf("派") >= 0) return "blue";
      return "red";
    },
    orderStatusShare(it) {
      const total = this.orderStatusTotal;
      const v = Number(it && it.value) || 0;
      if (!total || v <= 0) return "0%";
      return `${Math.round((100 * v) / total)}%`;
    },
    responseDelta(it, index) {
      const list = this.drawerList || [];
      if (!it || it.hours == null || index <= 0) return null;
      const prev = list[index - 1];
      if (!prev || prev.hours == null) return null;
      const diff = Math.round((Number(it.hours) - Number(prev.hours)) * 10) / 10;
      if (Math.abs(diff) < 0.05) return { tone: "flat", text: "持平" };
      if (diff < 0) return { tone: "up", text: `↑快 ${Math.abs(diff)}h` };
      return { tone: "down", text: `↓慢 ${diff}h` };
    },
    participateBarHeight(rate) {
      if (rate == null || !Number.isFinite(Number(rate))) return "8px";
      const v = Number(rate);
      if (v <= 0) return "8px";
      const max = this.participateBarMax || 1;
      const px = Math.max(10, Math.round((v / max) * 96));
      return `${px}px`;
    },
    participateYoy(it) {
      if (!it || it.yoy == null || !Number.isFinite(Number(it.yoy))) return null;
      const diff = Math.round(Number(it.yoy));
      if (diff === 0) return { tone: "flat", text: "同比持平" };
      if (diff > 0) return { tone: "up", text: `同比↑${diff}pt` };
      return { tone: "down", text: `同比↓${Math.abs(diff)}pt` };
    },
    statusTagClass(status) {
      const s = String(status || "");
      if (s.indexOf("落地") >= 0) return "tag--land";
      if (s.indexOf("批示") >= 0) return "tag--pi";
      if (
        s === "达标" ||
        s === "进行中" ||
        s === "已采纳" ||
        s === "已被区统战部采纳" ||
        s === "已办结" ||
        s === "已评单" ||
        s === "已接单" ||
        s === "已派单" ||
        s === "已点单" ||
        s === "推荐" ||
        s.indexOf("采纳") >= 0
      ) {
        return "tag--ok";
      }
      if (s === "关注" || s === "已完成" || s === "流动党员") return "tag--warn";
      if (s === "偏低" || s === "无场次" || s === "无建言") return "tag--bad";
      return "";
    },
    restoreLock() {
      try {
        this.locked = localStorage.getItem(LOCK_KEY) === "1";
      } catch (e) {
        this.locked = false;
      }
      this.lockPwdVisible = false;
      this.lockPwd = "";
      if (this.locked) {
        this.$nextTick(() => {
          this.$router.replace({ path: "/overview", query: {} });
        });
      }
    },
    restoreInspect() {
      try {
        this.inspectMode = localStorage.getItem(INSPECT_KEY) === "1";
      } catch (e) {
        this.inspectMode = false;
      }
      if (this.inspectMode && !this.locked) {
        const i = this.pages.findIndex(p => p.path === this.$route.path);
        if (i >= 0) this.pageIndex = i;
        this.startCarousel();
      }
    },
    persistInspect() {
      try {
        if (this.inspectMode) localStorage.setItem(INSPECT_KEY, "1");
        else localStorage.removeItem(INSPECT_KEY);
      } catch (e) {
        /* ignore */
      }
    },
    clearLockPwdIdle() {
      clearTimeout(this.lockPwdIdleTimer);
      this.lockPwdIdleTimer = null;
    },
    bumpLockPwdIdle() {
      this.clearLockPwdIdle();
      if (!this.locked || !this.lockPwdVisible) return;
      this.lockPwdIdleTimer = setTimeout(() => {
        this.closeUnlockDialog();
      }, LOCK_PWD_IDLE_MS);
    },
    openUnlockDialog() {
      if (!this.locked) return;
      this.lockPwd = "";
      this.lockPwdVisible = true;
    },
    closeUnlockDialog() {
      this.lockPwdVisible = false;
      this.lockPwd = "";
      this.clearLockPwdIdle();
    },
    setLocked(on) {
      this.locked = !!on;
      try {
        if (this.locked) localStorage.setItem(LOCK_KEY, "1");
        else localStorage.removeItem(LOCK_KEY);
      } catch (e) {
        /* ignore */
      }
      if (this.locked) {
        this.closeUnlockDialog();
        this.drawerVisible = false;
        this.welcomeVisible = false;
        this.onInspectRelease();
        if (this.voiceCommander && this.voiceOn) {
          this.voiceCommander.stop("lock");
        }
        this.$router.replace({ path: "/overview", query: {} });
        this.resetIdle();
      } else {
        this.closeUnlockDialog();
      }
    },
    restoreCache() {
      try {
        const key = this.homeCacheKey(this.$route.query.branchId);
        let raw = localStorage.getItem(key);
        // 兼容升级前旧缓存：仅在「全区」且新 key 没有时读一次
        if (
          !raw &&
          key === this.homeCacheKey(null) &&
          key !== CACHE_KEY_LEGACY
        ) {
          raw = localStorage.getItem(CACHE_KEY_LEGACY);
        }
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (data && data.code === 0) {
          if (isSameJson(this.home, data)) return true;
          this.home = data;
          this.$store.commit("updateScreenOverview", data);
          return true;
        }
      } catch (e) {
        /* ignore */
      }
      return false;
    },
    /** home 缓存按视角隔离：全区 all / 分会 {branchId} */
    homeCacheKey(branchId) {
      const raw = branchId == null ? "" : String(branchId).trim();
      const id = raw !== "" ? raw : "all";
      return `${CACHE_KEY_PREFIX}:${id}`;
    },
    tick() {
      const d = new Date();
      const pad = n => String(n).padStart(2, "0");
      this.clock = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    isActive(path) {
      return this.$route.path === path;
    },
    go(path) {
      if (this.locked) return;
      // 分会页始终展示全区排名；从分会详情切回「分会」时清掉 branchId
      if (path === "/branch") {
        if (this.$route.path !== "/branch" || this.$route.query.branchId) {
          this.$router.push({ path: "/branch", query: {} });
        }
        this.resetIdle();
        return;
      }
      if (this.$route.path !== path) {
        this.$router.push({ path, query: this.$route.query });
      }
      this.resetIdle();
    },
    clearBranch() {
      this.$router.push({ path: "/branch", query: {} });
    },
    /** 语音开启失败 / 运行中错误：提示面向生产，不写死本地端口 */
    showVoiceSetupWarning(reason, detail) {
      const host = (typeof location !== "undefined" && location.host) || "";
      const secure =
        typeof window !== "undefined" ? !!window.isSecureContext : false;
      if (reason === "unsupported") {
        Message.warning("当前浏览器不支持语音识别，请使用 Chrome");
        return;
      }
      if (reason === "insecure" || (reason === "mic-denied" && !secure)) {
        Message.warning(
          `语音需 HTTPS 安全上下文。当前 ${host || "本页"} 为非安全访问，浏览器会拒绝麦克风。请为站点配置 HTTPS 后再用 Chrome 打开`
        );
        return;
      }
      if (reason === "network") {
        Message.warning("语音识别网络失败，请确认演示机能访问外网语音服务");
        return;
      }
      if (reason === "service-not-allowed") {
        Message.warning("语音识别服务不可用，请检查网络或换 Chrome 再试");
        return;
      }
      if (reason === "mic-denied") {
        const name = (detail && detail.errorName) || "";
        if (name === "NotFoundError") {
          Message.warning("未检测到麦克风，请检查设备是否接入并启用");
          return;
        }
        if (name === "NotReadableError") {
          Message.warning("麦克风被其他程序占用，请关闭后重试");
          return;
        }
        Message.warning(
          "麦克风权限被拒绝。请点地址栏麦克风图标允许本站，或到 Chrome「设置 → 隐私和安全 → 网站设置 → 麦克风」中允许后刷新"
        );
        return;
      }
      if (reason === "not-allowed") {
        Message.warning(
          "麦克风权限被拒绝。请点地址栏麦克风图标允许本站后刷新再试"
        );
      }
    },
    async toggleVoice() {
      if (!this.voiceCommander) return;
      if (!this.voiceCommander.isSupported()) {
        this.showVoiceSetupWarning("unsupported");
        return;
      }
      if (this.voiceOn) {
        this.voiceCommander.stop("manual");
        return;
      }
      const res = await this.voiceCommander.start();
      if (!res || !res.ok) {
        this.showVoiceSetupWarning(res.reason || "mic-denied", res);
      }
    },
    onVoiceCommand(hit) {
      if (!hit || this.locked) return;
      const ok = this.applyVoiceAction(hit);
      if (!ok) return;
      this.showVoiceSuccess(hit.label);
      this.resetIdle();
      if (this.inspectMode) this.pauseCarouselForInteraction();
    },
    /** @returns {boolean} 是否真正执行（未执行则零反馈） */
    applyVoiceAction(hit) {
      const action = hit.action;
      if (action === "next" || action === "prev") {
        const list = this.carouselPages;
        if (!list.length) return false;
        const cur = this.$route.path;
        let i = list.findIndex(p => p.path === cur);
        if (i < 0) i = 0;
        const nextIdx =
          action === "next"
            ? (i + 1) % list.length
            : (i - 1 + list.length) % list.length;
        const next = list[nextIdx];
        if (!next) return false;
        const fullIdx = this.pages.findIndex(p => p.path === next.path);
        if (fullIdx >= 0) this.pageIndex = fullIdx;
        this.go(next.path);
        return true;
      }
      if (action === "home" || (action === "open" && hit.path === "/overview")) {
        this.go("/overview");
        return true;
      }
      if (action === "open" && hit.path) {
        this.go(hit.path);
        return true;
      }
      if (action === "enterInspect") {
        if (this.inspectMode) return false;
        this.enterInspect();
        return true;
      }
      if (action === "exitInspect") {
        if (!this.inspectMode || this.pwdVisible) return false;
        this.onInspectRelease();
        this.pwdVisible = true;
        this.exitPwd = "";
        return true;
      }
      return false;
    },
    showVoiceSuccess(label) {
      clearTimeout(this.voiceTipTimer);
      clearTimeout(this.voiceFlashTimer);
      this.voiceFlash = false;
      this.$nextTick(() => {
        this.voiceFlash = true;
        this.voiceFlashTimer = setTimeout(() => {
          this.voiceFlash = false;
        }, 600);
      });
      this.voiceTip = `识别成功：${label || ""}`;
      this.voiceTipTimer = setTimeout(() => {
        this.voiceTip = "";
      }, 1000);
    },
    stopHomeRefreshTimer() {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    },
    /** 仅当前为总览页时 10s 刷 home，避免停在其他页仍打 /home */
    syncHomeRefreshTimer() {
      this.stopHomeRefreshTimer();
      if (this.$route.path === "/overview") {
        this.refreshTimer = setInterval(() => this.loadHome({ fromPoll: true }), 10000);
      }
    },
    async loadHome(opts = {}) {
      // 轮询只服务总览；切页后残留定时器也直接丢弃
      if (opts.fromPoll && this.$route.path !== "/overview") {
        this.stopHomeRefreshTimer();
        return;
      }
      const branchId = this.$route.query.branchId;
      const cacheKey = this.homeCacheKey(branchId);
      try {
        const res = await this.$api("screen", "home", branchId ? { branchId } : {});
        if (res && res.code === 0) {
          if (isSameJson(this.home, res)) return;
          this.home = res;
          this.$store.commit("updateScreenOverview", res);
          localStorage.setItem(cacheKey, JSON.stringify(res));
        }
      } catch (e) {
        // 防重取消 / 主动 abort：不要回写缓存，否则会换新对象引用触发整页重绘
        if (
          (e && e.__dedupeCancelled) ||
          (e && e.message && /cancel/i.test(String(e.message)))
        ) {
          return;
        }
        // 仅在尚无可用 home 时回退缓存
        if (!(this.home && this.home.code === 0)) {
          this.restoreCache();
        }
      }
    },
    enterInspect() {
      this.inspectMode = true;
      this.persistInspect();
      this.go("/overview");
      this.welcomeVisible = true;
      this.stopCarousel();
    },
    dismissWelcome() {
      if (!this.welcomeVisible) return;
      this.welcomeVisible = false;
      if (this.inspectMode) this.startCarousel();
    },
    /** 是否允许迎检轮播推进（弹层 / 锁屏 / 欢迎页期间不切页） */
    canAdvanceCarousel() {
      return (
        this.inspectMode &&
        !this.welcomeVisible &&
        !this.pwdVisible &&
        !this.locked &&
        !this.drawerVisible
      );
    },
    startCarousel() {
      clearTimeout(this.carouselResumeTimer);
      this.carouselResumeTimer = null;
      clearInterval(this.carouselTimer);
      if (!this.canAdvanceCarousel()) return;
      const sec = (this.org.carouselSeconds || 12) * 1000;
      this.carouselTimer = setInterval(() => {
        if (!this.canAdvanceCarousel()) return;
        const list = this.carouselPages;
        if (!list.length) return;
        const cur = this.$route.path;
        let i = list.findIndex(p => p.path === cur);
        i = i < 0 ? 0 : (i + 1) % list.length;
        const next = list[i];
        const fullIdx = this.pages.findIndex(p => p.path === next.path);
        if (fullIdx >= 0) this.pageIndex = fullIdx;
        this.go(next.path);
      }, sec);
    },
    stopCarousel() {
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
      clearTimeout(this.carouselResumeTimer);
      this.carouselResumeTimer = null;
    },
    /** 用户操作 / 弹层：暂停轮播，并重新计时后续播 */
    pauseCarouselForInteraction() {
      if (!this.inspectMode) return;
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
      this.scheduleCarouselResume();
    },
    scheduleCarouselResume() {
      clearTimeout(this.carouselResumeTimer);
      this.carouselResumeTimer = null;
      if (!this.inspectMode) return;
      this.carouselResumeTimer = setTimeout(() => {
        this.tryResumeCarousel();
      }, CAROUSEL_RESUME_MS);
    },
    tryResumeCarousel() {
      if (!this.inspectMode) return;
      if (!this.canAdvanceCarousel()) {
        this.scheduleCarouselResume();
        return;
      }
      this.startCarousel();
    },
    onInspectPress() {
      if (!this.inspectMode || this.pwdVisible) return;
      this.onInspectRelease();
      this.inspectPressing = true;
      this.inspectPressProgress = 0;
      this.pressStartedAt = Date.now();
      const HOLD_MS = 3000;
      const tick = () => {
        const elapsed = Date.now() - this.pressStartedAt;
        this.inspectPressProgress = Math.min(100, (elapsed / HOLD_MS) * 100);
        if (elapsed >= HOLD_MS) {
          this.onInspectRelease();
          this.pwdVisible = true;
          this.exitPwd = "";
          return;
        }
        this.pressRaf = requestAnimationFrame(tick);
      };
      this.pressRaf = requestAnimationFrame(tick);
      this.pressTimer = setTimeout(() => {
        /* 兜底：部分环境 rAF 被节流时仍能弹出 */
        if (!this.pwdVisible && this.inspectPressing) {
          this.onInspectRelease();
          this.pwdVisible = true;
          this.exitPwd = "";
        }
      }, HOLD_MS + 50);
    },
    onInspectRelease() {
      clearTimeout(this.pressTimer);
      this.pressTimer = null;
      if (this.pressRaf) {
        cancelAnimationFrame(this.pressRaf);
        this.pressRaf = null;
      }
      this.inspectPressing = false;
      this.inspectPressProgress = 0;
      this.pressStartedAt = 0;
    },
    async confirmExitInspect() {
      const res = await this.$api("screen", "verifyInspect", { password: this.exitPwd });
      if (res && res.ok) {
        this.inspectMode = false;
        this.welcomeVisible = false;
        this.persistInspect();
        this.stopCarousel();
        this.pwdVisible = false;
        this.onInspectRelease();
        Message.success("已退出迎检");
      } else {
        Message.error("密码错误");
      }
    },
    async unlock() {
      this.bumpLockPwdIdle();
      const res = await this.$api("screen", "verifyInspect", { password: this.lockPwd });
      if (res && res.ok) {
        this.setLocked(false);
        Message.success("已解锁");
      } else {
        Message.error("密码错误");
      }
    },
    openDrawer({ title, list, items, sections, summary, summaryLayout, listLayout, infinite, hasMore, searchable, heroIcon, listHeading }) {
      this.drawerTitle = title || "详情";
      let rich = [];
      if (sections && sections.length) {
        sections.forEach(sec => {
          const heading = (sec && sec.title) || "";
          if (heading) {
            rich.push({ _section: true, title: heading });
          }
          (sec.items || [])
            .map(toDetailItem)
            .filter(Boolean)
            .forEach(it => rich.push(it));
        });
      } else {
        rich = (items || []).map(toDetailItem).filter(Boolean);
      }
      this.drawerItems = rich;
      this.drawerList = list || [];
      this.drawerSummary = summary || [];
      this.drawerSummaryLayout = summaryLayout || "";
      this.drawerListLayout = listLayout || "";
      this.drawerHeroIcon = heroIcon || "";
      this.drawerListHeading = listHeading || "";
      this.drawerInfinite = infinite || null;
      this.drawerSkip = rich.filter(it => !it._section).length;
      this.drawerHasMore = !!(infinite && hasMore);
      this.drawerLoading = false;
      this.drawerBodyExpanded = {};
      this.drawerSearchable = !!searchable;
      this.drawerKeyword = "";
      this.drawerVisible = true;
      if (this.inspectMode) this.pauseCarouselForInteraction();
      this.$nextTick(() => {
        const el = this.$refs.drawerScroll;
        if (el) el.scrollTop = 0;
      });
    },
    onDrawerClosed() {
      this.drawerInfinite = null;
      this.drawerSkip = 0;
      this.drawerHasMore = false;
      this.drawerLoading = false;
      this.drawerSummary = [];
      this.drawerSummaryLayout = "";
      this.drawerListLayout = "";
      this.drawerHeroIcon = "";
      this.drawerListHeading = "";
      this.drawerBodyExpanded = {};
      this.drawerSearchable = false;
      this.drawerKeyword = "";
    },
    async onDrawerSearch() {
      if (!this.drawerInfinite || typeof this.drawerInfinite.fetch !== "function") return;
      this.drawerLoading = true;
      try {
        const take = this.drawerInfinite.pageSize || 20;
        const res = await this.drawerInfinite.fetch(0, take, this.drawerKeyword || "");
        const more = ((res && res.items) || []).map(toDetailItem).filter(Boolean);
        this.drawerItems = more;
        this.drawerSkip = more.length;
        this.drawerHasMore = !!(res && res.hasMore);
        if (Array.isArray(res && res.summary)) {
          this.drawerSummary = res.summary;
        }
        this.$nextTick(() => {
          const el = this.$refs.drawerScroll;
          if (el) el.scrollTop = 0;
        });
      } catch (e) {
        this.drawerItems = [];
        this.drawerHasMore = false;
      } finally {
        this.drawerLoading = false;
      }
    },
    needBodyToggle(content) {
      return String(content || "").trim().length > 180;
    },
    isBodyCollapsed(index, content) {
      if (!this.needBodyToggle(content)) return false;
      return !this.drawerBodyExpanded[index];
    },
    toggleBody(index) {
      this.$set(this.drawerBodyExpanded, index, !this.drawerBodyExpanded[index]);
    },
    thumbPreviewList(thumbs) {
      return (thumbs || [])
        .map(t => this.absUrl(t.coverUrl || t.url || t))
        .filter(Boolean);
    },
    async onDrawerScroll(e) {
      if (!this.drawerInfinite || this.drawerLoading || !this.drawerHasMore) return;
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight > 100) return;
      const fetchMore = this.drawerInfinite.fetch;
      if (typeof fetchMore !== "function") return;
      this.drawerLoading = true;
      try {
        const take = this.drawerInfinite.pageSize || 20;
        const res = await fetchMore(this.drawerSkip, take, this.drawerKeyword || "");
        const more = ((res && res.items) || []).map(toDetailItem).filter(Boolean);
        if (more.length) {
          this.drawerItems = this.drawerItems.concat(more);
          this.drawerSkip += more.length;
        }
        this.drawerHasMore = !!(res && res.hasMore);
      } catch (err) {
        this.drawerHasMore = false;
      } finally {
        this.drawerLoading = false;
      }
    },
    async onShot() {
      try {
        const canvas = await html2canvas(this.$el, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#020b1e"
        });
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `xinlianhui-screen-${Date.now()}.png`;
        a.click();
      } catch (e) {
        Message.error("截图失败");
      }
    },
    bindIdle() {
      this.resetIdle = this.resetIdle.bind(this);
      window.addEventListener("pointerdown", this.resetIdle);
      this.resetIdle();
    },
    resetIdle() {
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        if (!this.inspectMode && this.$route.path !== "/overview") {
          this.go("/overview");
        }
      }, 5 * 60 * 1000);
      // 迎检中：任意按下暂停轮播，约 60 秒无操作后再续播
      if (this.inspectMode) {
        this.pauseCarouselForInteraction();
      }
    }
  }
};
</script>
<style lang="less" scoped>
@import "~@/assets/less/dash-theme.less";

.dash-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  .dash-hex-bg();
  color: #fff;
  position: relative;
}
.dash-mid {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  /* 与原 dash-main 上下内边距一致，保证侧栏与右侧内容区顶底对齐 */
  padding: 10 / @rem2 0 8 / @rem2;
  box-sizing: border-box;
}
.dash-side {
  width: 88 / @rem2;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding: 8 / @rem2 12 / @rem2 8 / @rem2 8 / @rem2;
  gap: 8 / @rem2;
  position: relative;
  z-index: 2;
  /* 设计稿取样纯色 #000D1B，均匀无噪点 */
  background-color: #000d1b;
  background-image: linear-gradient(
    90deg,
    rgba(0, 100, 180, 0.18) 0%,
    transparent 10%
  );
  border-right: 0;
  /* 右侧用青光阴影代替实线边框 */
  box-shadow:
    inset -6 / @rem2 0 10 / @rem2 rgba(0, 160, 255, 0.12),
    4 / @rem2 0 18 / @rem2 rgba(0, 0, 0, 0.4);
}
.dash-side__item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6 / @rem2;
  height: 72 / @rem2;
  padding: 0 4 / @rem2;
  border: 1 / @rem2 solid transparent;
  border-radius: 6 / @rem2;
  background: transparent;
  color: #8aa9c4;
  font-size: 13 / @rem2;
  cursor: pointer;
  transition: color 0.15s, background 0.15s, box-shadow 0.15s, border-color 0.15s;
  .dash-side__icon {
    font-size: 26 / @rem2;
    line-height: 1;
    color: inherit;
  }
  .dash-side__label {
    line-height: 1;
    letter-spacing: 1 / @rem2;
  }
  &:hover {
    color: #b7d8ef;
    background: rgba(0, 120, 200, 0.12);
  }
  /* 设计稿选中：发光底块 + 顶部亮线 */
  &.active {
    color: #7ee6ff;
    border-color: rgba(80, 190, 255, 0.35);
    background:
      linear-gradient(180deg, rgba(60, 170, 255, 0.28) 0%, transparent 18%),
      radial-gradient(
        ellipse 80% 70% at 50% 42%,
        rgba(40, 160, 255, 0.42) 0%,
        rgba(0, 100, 180, 0.12) 55%,
        transparent 78%
      );
    box-shadow:
      0 0 14 / @rem2 rgba(0, 170, 255, 0.28),
      inset 0 0 12 / @rem2 rgba(0, 160, 255, 0.12);
    .dash-side__icon {
      filter: drop-shadow(0 0 4px rgba(100, 210, 255, 0.55));
    }
    .dash-side__label {
      text-shadow: 0 0 6 / @rem2 rgba(80, 200, 255, 0.35);
    }
  }
}
.dash-body {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}
.lock-block {
  position: absolute;
  inset: 0;
  z-index: 40;
  cursor: default;
  background: transparent;
}
.dash-header {
  position: relative;
  height: 92 / @rem2;
  display: flex;
  align-items: center; /* 左中右整体垂直居中 */
  padding: 0 18 / @rem2;
  /* 底部留出装饰条高度，让文字落在「标题区」视觉中心，而不是被装饰条拉偏 */
  padding-bottom: 28 / @rem2;
  box-sizing: border-box;
  background:
    radial-gradient(ellipse 70% 90% at 50% 100%, rgba(0, 140, 220, 0.18) 0%, transparent 55%),
    linear-gradient(180deg, #0a2148 0%, #061530 48%, #030d20 100%);
  overflow: hidden;
}
.dash-header__ornament {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 34 / @rem2;
  background-image: var(--hdr-ornament);
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: 100% 100%;
  pointer-events: none;
  z-index: 1;
}
.dash-header__left {
  width: 26%;
  display: flex;
  align-items: center;
  gap: 10 / @rem2;
  min-width: 0;
  color: #d7e6ff;
  position: relative;
  z-index: 2;
}
.dash-header__center {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 12 / @rem2;
  position: relative;
  z-index: 2;
}
.dash-header__right {
  width: 28%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8 / @rem2;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}
.dash-header--branch {
  .dash-header__left {
    width: 22%;
  }
  .dash-header__right {
    width: 36%;
  }
}
.org-name {
  font-size: 15 / @rem2;
  font-weight: 600;
  color: #f0f7ff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 0 8 / @rem2 rgba(0, 180, 255, 0.25);
}
.header-title {
  margin: 0;
  font-size: 28 / @rem2;
  font-weight: 700;
  letter-spacing: 2 / @rem2;
  line-height: 1.2;
  color: #fff;
  text-align: center;
  text-shadow:
    0 0 18 / @rem2 fade(@mainColor, 55%),
    0 0 4 / @rem2 rgba(0, 200, 255, 0.35);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.logo-img {
  width: 44 / @rem2;
  height: 44 / @rem2;
  object-fit: contain;
  object-position: center;
  flex-shrink: 0;
  background: transparent;
}
.branch-chip {
  display: inline-flex;
  align-items: center;
  gap: 6 / @rem2;
  max-width: 220 / @rem2;
  padding: 0 10 / @rem2;
  height: 32 / @rem2;
  border-radius: 4 / @rem2;
  border: 1 / @rem2 solid fade(#36d7b7, 40%);
  background: rgba(0, 80, 60, 0.35);
  color: #7dffc8;
  font-size: 13 / @rem2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  i {
    flex-shrink: 0;
    font-size: 14 / @rem2;
  }
}
.drawer-scroll {
  flex: 1;
  min-height: 0;
  height: auto;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 24px;
  overscroll-behavior: contain;
}
.drawer-search {
  position: sticky;
  top: 0;
  z-index: 2;
  margin: 0 16px 10px;
  padding-top: 4px;
  background: linear-gradient(180deg, #f7fbff 75%, transparent);
}
.drawer-search /deep/ .el-input__inner {
  background: #fff;
  border-color: #c5daf5;
  color: #1f4f8a;
}
.drawer-search /deep/ .el-input-group__append {
  background: #e8f3ff;
  border-color: #c5daf5;
  color: #1f4f8a;
}
.drawer-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 16px 14px;
  padding: 0;
  list-style: none;
  li {
    flex: 1 1 auto;
    min-width: 140px;
    padding: 12px 14px;
    border-radius: 10px;
    background: linear-gradient(135deg, #eef6ff 0%, #f5f9ff 100%);
    border: 1px solid #d6e6fb;
    color: #1f4f8a;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
  }
  &--objects:not(.drawer-summary--rows) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    li {
      min-width: 0;
      flex: none;
      display: flex;
      flex-direction: column;
      gap: 6px;
      &.is-wide {
        grid-column: 1 / -1;
      }
      &.is-accent {
        background: linear-gradient(135deg, #ffe8e8 0%, #fff5f5 100%);
        border-color: #ffc5c5;
        color: #c0392b;
        .drawer-summary__value {
          color: #c0392b;
        }
        .drawer-summary__label {
          color: #d47878;
        }
      }
    }
  }
  /* 明细行：左标签右数值，不用宫格卡片 */
  &--rows {
    display: block;
    gap: 0;
    margin: 0 16px 20px;
    padding: 4px 0;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #e5edf7;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(26, 63, 115, 0.04);
    li {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex: none;
      min-width: 0;
      min-height: 44px;
      margin: 0;
      padding: 11px 16px;
      border: none;
      border-radius: 0;
      border-bottom: 1px solid #eef3f9;
      background: transparent;
      box-sizing: border-box;
      &:last-child {
        border-bottom: none;
      }
      &.is-wide {
        grid-column: auto;
      }
      &.is-accent {
        background: #fff7f7;
        .drawer-summary__label {
          color: #c0392b;
          font-weight: 600;
        }
        .drawer-summary__value {
          color: #c0392b;
          font-size: 15px;
        }
      }
    }
    .drawer-summary__label {
      flex-shrink: 0;
      font-size: 13px;
      color: #5a7394;
    }
    .drawer-summary__value {
      text-align: right;
      font-size: 15px;
      font-weight: 600;
      color: #1a3f73;
      word-break: break-all;
    }
  }
}
.drawer-summary__label {
  font-style: normal;
  font-size: 12px;
  font-weight: 500;
  color: #6a86a8;
  letter-spacing: 0.02em;
}
.drawer-summary__value {
  font-size: 20px;
  font-weight: 700;
  color: #1a3f73;
  line-height: 1.2;
  word-break: break-all;
}
.drawer-section__title {
  margin: 18px 0 10px;
  padding: 0 2px;
  font-size: 14px;
  font-weight: 700;
  color: #1f4f8a;
  letter-spacing: 0.02em;
  &:first-child {
    margin-top: 4px;
  }
  &::before {
    content: "";
    display: inline-block;
    width: 3px;
    height: 12px;
    margin-right: 8px;
    border-radius: 2px;
    background: #3d8bff;
    vertical-align: -1px;
  }
}
.drawer-list {
  margin: 0 16px 16px;
  padding: 0;
  list-style: none;
  li {
    margin-bottom: 8px;
    padding: 12px 14px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid #e8eef6;
    box-shadow: 0 4px 12px rgba(15, 40, 80, 0.04);
    color: #303133;
    font-size: 14px;
    line-height: 1.6;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
.drawer-points {
  margin: 0 16px 16px;
  &__hero {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
    padding: 20px 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, #1a6dff 0%, #3d8bff 52%, #5cb3ff 100%);
    box-shadow: 0 8px 24px rgba(26, 109, 255, 0.28);
    overflow: hidden;
  }
  &__hero-glow {
    position: absolute;
    top: -30%;
    right: -10%;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22) 0%, transparent 70%);
    pointer-events: none;
  }
  &__hero-icon {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    i {
      font-size: 26px;
      color: #ffe066;
      filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.15));
    }
  }
  &__hero-body {
    position: relative;
    z-index: 1;
    min-width: 0;
    em {
      display: block;
      font-style: normal;
      font-size: 13px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.82);
      letter-spacing: 0.04em;
    }
    strong {
      display: block;
      margin-top: 4px;
      font-size: 32px;
      font-weight: 700;
      color: #fff;
      line-height: 1.1;
      letter-spacing: -0.02em;
    }
  }
  &__heading {
    margin: 0 0 12px;
    padding: 0 2px;
    font-size: 13px;
    font-weight: 600;
    color: #6a86a8;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    &::before {
      content: "";
      display: inline-block;
      width: 3px;
      height: 12px;
      margin-right: 8px;
      border-radius: 2px;
      background: linear-gradient(180deg, #3d8bff, #1a6dff);
      vertical-align: -1px;
    }
  }
  &__rules {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
    li {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 14px;
      border-radius: 12px;
      background: #fff;
      border: 1px solid #e8eef6;
      box-shadow: 0 2px 10px rgba(15, 40, 80, 0.05);
      transition: box-shadow 0.2s, transform 0.2s;
      &:hover {
        box-shadow: 0 6px 18px rgba(26, 109, 255, 0.1);
        transform: translateY(-1px);
      }
    }
  }
  &__icon {
    --rule-color: #3d8bff;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: var(--rule-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    i {
      font-size: 18px;
      color: #fff;
    }
  }
  &__name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    line-height: 1.45;
  }
  &__badge {
    flex-shrink: 0;
    padding: 5px 10px;
    border-radius: 20px;
    background: linear-gradient(135deg, #fff8e6 0%, #fff3cc 100%);
    border: 1px solid #f5dfa0;
    font-size: 15px;
    font-weight: 700;
    color: #d48806;
    line-height: 1;
    small {
      margin-left: 1px;
      font-size: 11px;
      font-weight: 600;
    }
  }
  .tone-0 { --rule-color: #3d8bff; }
  .tone-1 { --rule-color: #36d7b7; }
  .tone-2 { --rule-color: #f5c542; }
  .tone-3 { --rule-color: #ff8a3d; }
  .tone-4 { --rule-color: #a78bfa; }
}
.drawer-active {
  margin: 0 16px 16px;
  &__hero {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 14px;
    padding: 20px 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, #0f9f8a 0%, #36d7b7 55%, #6ee7c8 100%);
    box-shadow: 0 8px 24px rgba(54, 215, 183, 0.28);
    overflow: hidden;
  }
  &__hero-glow {
    position: absolute;
    top: -30%;
    right: -8%;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.24) 0%, transparent 70%);
    pointer-events: none;
  }
  &__hero-icon {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    i {
      font-size: 26px;
      color: #fff;
      filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.12));
    }
  }
  &__hero-body {
    position: relative;
    z-index: 1;
    min-width: 0;
    em {
      display: block;
      font-style: normal;
      font-size: 13px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.85);
      letter-spacing: 0.04em;
    }
    strong {
      display: block;
      margin-top: 4px;
      font-size: 32px;
      font-weight: 700;
      color: #fff;
      line-height: 1.1;
    }
  }
  &__rule {
    margin: 0 0 16px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #eefaf6;
    border: 1px solid #cfeee4;
    font-size: 12px;
    color: #4a8a7a;
    line-height: 1.5;
  }
  &__heading {
    margin: 0 0 12px;
    padding: 0 2px;
    font-size: 13px;
    font-weight: 600;
    color: #6a86a8;
    letter-spacing: 0.04em;
    &::before {
      content: "";
      display: inline-block;
      width: 3px;
      height: 12px;
      margin-right: 8px;
      border-radius: 2px;
      background: linear-gradient(180deg, #36d7b7, #0f9f8a);
      vertical-align: -1px;
    }
  }
  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
    li {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      border-radius: 12px;
      background: #fff;
      border: 1px solid #e8eef6;
      box-shadow: 0 2px 10px rgba(15, 40, 80, 0.05);
    }
  }
  &__avatar {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #36d7b7, #1a6dff) center/cover no-repeat;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    overflow: hidden;
  }
  &__meta {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      font-size: 15px;
      font-weight: 600;
      color: #1f2d3d;
      line-height: 1.3;
    }
    em {
      display: block;
      margin-top: 2px;
      font-style: normal;
      font-size: 12px;
      color: #8a9bb0;
    }
  }
  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }
  &__tag {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.5;
    &.tag-meet {
      background: #eef4ff;
      color: #3d8bff;
    }
    &.tag-learn {
      background: #f3eeff;
      color: #8b6cff;
    }
    &.tag-vol {
      background: #e9faf5;
      color: #1aaa8c;
    }
    &.tag-sandan {
      background: #fff3e8;
      color: #e67e22;
    }
    &.tag-brand {
      background: #ffe8ee;
      color: #e74c6f;
    }
    &.tag-default {
      background: #f0f3f8;
      color: #6a86a8;
    }
  }
  &__idx {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    color: #b8c5d6;
  }
  &__empty {
    margin: 0;
    padding: 28px 16px;
    text-align: center;
    color: #909399;
    font-size: 14px;
    background: #fff;
    border: 1px dashed #dce4f0;
    border-radius: 12px;
  }
}
.drawer-bucket {
  margin: 0 16px 16px;
  &__hero {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
    padding: 20px 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, #1a5fd6 0%, #3d8bff 55%, #7ac4ff 100%);
    box-shadow: 0 8px 24px rgba(61, 139, 255, 0.3);
    overflow: hidden;
  }
  &__hero-glow {
    position: absolute;
    top: -30%;
    right: -8%;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.28) 0%,
      transparent 70%
    );
    pointer-events: none;
  }
  &__hero-icon {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    i {
      font-size: 26px;
      color: #fff;
    }
  }
  &__hero-body {
    position: relative;
    z-index: 1;
    min-width: 0;
    em {
      display: block;
      font-style: normal;
      font-size: 13px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.88);
      letter-spacing: 0.04em;
    }
    strong {
      display: block;
      margin-top: 4px;
      font-size: 32px;
      font-weight: 700;
      color: #fff;
      line-height: 1.1;
    }
  }
  &__range {
    margin: 0 0 16px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #eef5ff;
    border: 1px solid #d0e2fb;
    font-size: 12px;
    color: #5a7aa8;
    line-height: 1.5;
    b {
      margin-left: 4px;
      color: #1a5fd6;
      font-weight: 700;
    }
  }
  &__heading {
    margin: 0 0 12px;
    padding: 0 2px;
    font-size: 13px;
    font-weight: 600;
    color: #6a86a8;
    letter-spacing: 0.04em;
    &::before {
      content: "";
      display: inline-block;
      width: 3px;
      height: 12px;
      margin-right: 8px;
      border-radius: 2px;
      background: linear-gradient(180deg, #7ac4ff, #1a5fd6);
      vertical-align: -1px;
    }
  }
  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
    li {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      border-radius: 12px;
      background: #fff;
      border: 1px solid #e8eef6;
      box-shadow: 0 2px 10px rgba(15, 40, 80, 0.05);
    }
  }
  &__avatar {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #3d8bff, #1a5fd6) center/cover no-repeat;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    overflow: hidden;
  }
  &__meta {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      font-size: 15px;
      font-weight: 600;
      color: #1f2d3d;
      line-height: 1.3;
    }
    em {
      display: block;
      margin-top: 2px;
      font-style: normal;
      font-size: 12px;
      color: #8a9bb0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  &__points {
    flex-shrink: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1a5fd6;
    line-height: 1;
    small {
      margin-left: 2px;
      font-size: 11px;
      font-weight: 600;
      color: #7a9cc8;
    }
  }
  &__empty {
    margin: 0;
    padding: 28px 16px;
    text-align: center;
    color: #909399;
    font-size: 14px;
    background: #fff;
    border: 1px dashed #dce4f0;
    border-radius: 12px;
  }
}
.drawer-order-status {
  margin: 0 16px 16px;
  &__hero {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 14px;
    padding: 20px 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, #1a6dff 0%, #00c3ff 52%, #5ad6ff 100%);
    box-shadow: 0 8px 24px rgba(0, 150, 255, 0.28);
    overflow: hidden;
  }
  &__hero-glow {
    position: absolute;
    top: -28%;
    right: -8%;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.28) 0%, transparent 70%);
    pointer-events: none;
  }
  &__hero-icon {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    i {
      font-size: 26px;
      color: #fff;
    }
  }
  &__hero-body {
    position: relative;
    z-index: 1;
    em {
      display: block;
      font-style: normal;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.88);
    }
    strong {
      display: block;
      margin-top: 4px;
      font-size: 32px;
      font-weight: 700;
      color: #fff;
      line-height: 1.1;
    }
  }
  &__sub {
    margin: 0 0 8px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13px;
    color: #5a7394;
    background: #f4f7fb;
    border: 1px solid #e0e8f2;
    b {
      margin-left: 8px;
      font-weight: 700;
      color: #1f2d3d;
    }
  }
  &__heading {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 600;
    color: #6a86a8;
    &::before {
      content: "";
      display: inline-block;
      width: 3px;
      height: 12px;
      margin-right: 8px;
      border-radius: 2px;
      background: linear-gradient(180deg, #5ad6ff, #1a6dff);
      vertical-align: -1px;
    }
  }
  &__chart {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 8px;
    height: 140px;
    margin-bottom: 16px;
    padding: 14px 12px 10px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #e8eef6;
    box-shadow: 0 2px 10px rgba(15, 40, 80, 0.04);
  }
  &__col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    height: 100%;
  }
  &__val {
    font-size: 11px;
    font-weight: 600;
    color: #3d6ea8;
    line-height: 1;
  }
  &__bar {
    width: 56%;
    max-width: 28px;
    min-height: 8px;
    border-radius: 6px 6px 2px 2px;
    &.tone-red {
      background: linear-gradient(180deg, #ff8a8a 0%, #ff5a5a 100%);
      box-shadow: 0 2px 8px rgba(255, 90, 90, 0.28);
    }
    &.tone-blue {
      background: linear-gradient(180deg, #5ad6ff 0%, #1a6dff 100%);
      box-shadow: 0 2px 8px rgba(26, 109, 255, 0.28);
    }
    &.tone-green {
      background: linear-gradient(180deg, #6eebcc 0%, #36d7b7 100%);
      box-shadow: 0 2px 8px rgba(54, 215, 183, 0.28);
    }
    &.tone-gold {
      background: linear-gradient(180deg, #f5c542 0%, #d97706 100%);
      box-shadow: 0 2px 8px rgba(245, 158, 11, 0.28);
    }
  }
  &__month {
    font-size: 11px;
    color: #8a9bb0;
    white-space: nowrap;
  }
  &__list {
    margin: 0 0 8px;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    li {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 11px 14px;
      border-radius: 10px;
      background: #fff;
      border: 1px solid #e8eef6;
    }
    strong {
      width: 56px;
      flex-shrink: 0;
      font-size: 14px;
      color: #1f2d3d;
    }
  }
  &__meta {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: #5a7394;
  }
  &__share {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 600;
    &.tone-red { color: #e85a5a; }
    &.tone-blue { color: #1a6dff; }
    &.tone-green { color: #0f9f8a; }
    &.tone-gold { color: #d48806; }
  }
  &__empty {
    margin: 0;
    padding: 16px;
    text-align: center;
    color: #909399;
    font-size: 13px;
  }
}
.drawer-response {
  margin: 0 16px 16px;
  &__hero {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 14px;
    padding: 20px 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, #d97706 0%, #f59e0b 48%, #fbbf24 100%);
    box-shadow: 0 8px 24px rgba(245, 158, 11, 0.28);
    overflow: hidden;
  }
  &__hero-glow {
    position: absolute;
    top: -28%;
    right: -8%;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.28) 0%, transparent 70%);
    pointer-events: none;
  }
  &__hero-icon {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    i {
      font-size: 26px;
      color: #fff;
    }
  }
  &__hero-body {
    position: relative;
    z-index: 1;
    em {
      display: block;
      font-style: normal;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.88);
    }
    strong {
      display: block;
      margin-top: 4px;
      font-size: 32px;
      font-weight: 700;
      color: #fff;
      line-height: 1.1;
    }
  }
  &__verdict {
    margin: 0 0 16px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    &.is-up {
      background: #e9faf5;
      color: #0f9f8a;
      border: 1px solid #b7ebd9;
    }
    &.is-down {
      background: #fff1f0;
      color: #d4380d;
      border: 1px solid #ffccc7;
    }
    &.is-flat {
      background: #f4f7fb;
      color: #5a7394;
      border: 1px solid #e0e8f2;
    }
  }
  &__heading {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 600;
    color: #6a86a8;
    &::before {
      content: "";
      display: inline-block;
      width: 3px;
      height: 12px;
      margin-right: 8px;
      border-radius: 2px;
      background: linear-gradient(180deg, #fbbf24, #d97706);
      vertical-align: -1px;
    }
  }
  &__chart {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 8px;
    height: 140px;
    margin-bottom: 16px;
    padding: 14px 12px 10px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #e8eef6;
    box-shadow: 0 2px 10px rgba(15, 40, 80, 0.04);
  }
  &__col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    height: 100%;
  }
  &__val {
    font-size: 11px;
    font-weight: 600;
    color: #d48806;
    line-height: 1;
  }
  &__bar {
    width: 56%;
    max-width: 28px;
    min-height: 8px;
    border-radius: 6px 6px 2px 2px;
    background: linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%);
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.28);
    &.is-empty {
      background: #e8eef6;
      box-shadow: none;
    }
  }
  &__month {
    font-size: 11px;
    color: #8a9bb0;
    white-space: nowrap;
  }
  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    li {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 11px 14px;
      border-radius: 10px;
      background: #fff;
      border: 1px solid #e8eef6;
    }
    strong {
      width: 48px;
      flex-shrink: 0;
      font-size: 14px;
      color: #1f2d3d;
    }
  }
  &__meta {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: #5a7394;
    em {
      font-style: normal;
      color: #9aa8bc;
    }
  }
  &__delta {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 600;
    &.is-up { color: #0f9f8a; }
    &.is-down { color: #d4380d; }
    &.is-flat { color: #8a9bb0; }
  }
  &__empty {
    margin: 0;
    padding: 28px 16px;
    text-align: center;
    color: #909399;
    font-size: 14px;
    background: #fff;
    border: 1px dashed #dce4f0;
    border-radius: 12px;
  }
}
.drawer-participate {
  margin: 0 16px 16px;
  &__hero {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 14px;
    padding: 20px 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 48%, #38bdf8 100%);
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.28);
    overflow: hidden;
  }
  &__hero-glow {
    position: absolute;
    top: -28%;
    right: -8%;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.28) 0%, transparent 70%);
    pointer-events: none;
  }
  &__hero-icon {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    i {
      font-size: 26px;
      color: #fff;
    }
  }
  &__hero-body {
    position: relative;
    z-index: 1;
    em {
      display: block;
      font-style: normal;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.88);
    }
    strong {
      display: block;
      margin-top: 4px;
      font-size: 32px;
      font-weight: 700;
      color: #fff;
      line-height: 1.1;
    }
  }
  &__verdict {
    margin: 0 0 16px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    &.is-up {
      background: #e9faf5;
      color: #0f9f8a;
      border: 1px solid #b7ebd9;
    }
    &.is-down {
      background: #fff1f0;
      color: #d4380d;
      border: 1px solid #ffccc7;
    }
    &.is-flat {
      background: #f4f7fb;
      color: #5a7394;
      border: 1px solid #e0e8f2;
    }
  }
  &__heading {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 600;
    color: #6a86a8;
    &::before {
      content: "";
      display: inline-block;
      width: 3px;
      height: 12px;
      margin-right: 8px;
      border-radius: 2px;
      background: linear-gradient(180deg, #38bdf8, #1d4ed8);
      vertical-align: -1px;
    }
  }
  &__chart {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 8px;
    height: 140px;
    margin-bottom: 10px;
    padding: 14px 12px 10px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #e8eef6;
    box-shadow: 0 2px 10px rgba(15, 40, 80, 0.04);
  }
  &__col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    height: 100%;
  }
  &__val {
    font-size: 11px;
    font-weight: 600;
    color: #2563eb;
    line-height: 1;
  }
  &__bars {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 3px;
    width: 100%;
    height: 100%;
  }
  &__bar {
    width: 10px;
    min-height: 8px;
    border-radius: 4px 4px 2px 2px;
    &--now {
      background: linear-gradient(180deg, #38bdf8 0%, #2563eb 100%);
      box-shadow: 0 2px 8px rgba(37, 99, 235, 0.28);
    }
    &--ly {
      background: #c5d4e8;
      box-shadow: none;
    }
  }
  &__month {
    font-size: 11px;
    color: #8a9bb0;
    white-space: nowrap;
  }
  &__legend {
    display: flex;
    gap: 16px;
    margin: 0 0 14px;
    padding: 0 4px;
    font-size: 12px;
    color: #8a9bb0;
    span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    i {
      display: inline-block;
      width: 10px;
      height: 10px;
      border-radius: 2px;
      &.is-now {
        background: linear-gradient(180deg, #38bdf8, #2563eb);
      }
      &.is-ly {
        background: #c5d4e8;
      }
    }
  }
  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    li {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 11px 14px;
      border-radius: 10px;
      background: #fff;
      border: 1px solid #e8eef6;
    }
    strong {
      width: 48px;
      flex-shrink: 0;
      font-size: 14px;
      color: #1f2d3d;
    }
  }
  &__meta {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: #5a7394;
    em {
      font-style: normal;
      color: #9aa8bc;
    }
  }
  &__ly {
    white-space: nowrap;
  }
  &__delta {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 600;
    &.is-up { color: #0f9f8a; }
    &.is-down { color: #d4380d; }
    &.is-flat { color: #8a9bb0; }
  }
  &__empty {
    margin: 0;
    padding: 28px 16px;
    text-align: center;
    color: #909399;
    font-size: 14px;
    background: #fff;
    border: 1px dashed #dce4f0;
    border-radius: 12px;
  }
}
.drawer-rich {
  padding: 0 16px 16px;
}
.drawer-card {
  margin-bottom: 16px;
  border: 1px solid #e8eef6;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 6px 18px rgba(15, 40, 80, 0.06);
  &:last-child {
    margin-bottom: 0;
  }
}
.drawer-card__cover {
  position: relative;
  height: 168px;
  background: #d9e6f5 center / cover no-repeat;
}
.drawer-card__cover-shade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 48px;
  background: linear-gradient(180deg, transparent, rgba(8, 24, 48, 0.28));
  pointer-events: none;
}
.drawer-card__main {
  padding: 14px 16px 16px;
}
.drawer-thumbs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 2px;
}
.drawer-thumbs--after {
  margin-top: 12px;
}
.drawer-thumbs__label {
  grid-column: 1 / -1;
  margin: 0 0 4px;
  font-size: 12px;
  color: #7a93b5;
}
.drawer-thumbs__item {
  width: 100%;
  height: 64px;
  border-radius: 6px;
  overflow: hidden;
  background: #eef3f9;
  cursor: pointer;
}
.drawer-thumbs__item /deep/ img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.drawer-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  .tag {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 11px;
    font-size: 12px;
    line-height: 1;
    color: #5b6f8c;
    background: #f0f4f9;
    &--cat {
      color: #1f6fd6;
      background: rgba(64, 158, 255, 0.12);
    }
    &--time {
      color: #3a8fd6;
      background: rgba(126, 200, 255, 0.18);
    }
    &--ok {
      color: #1a8f6a;
      background: rgba(54, 215, 183, 0.18);
    }
    &--pi {
      color: #b07a12;
      background: rgba(245, 197, 66, 0.2);
    }
    &--land {
      color: #1a7aa8;
      background: rgba(122, 231, 255, 0.22);
    }
    &--hl {
      color: #9a6b00;
      background: rgba(245, 197, 66, 0.28);
      font-weight: 600;
    }
    &--warn {
      color: #b07a12;
      background: rgba(245, 197, 66, 0.22);
    }
    &--bad {
      color: #c45a2a;
      background: rgba(255, 138, 61, 0.18);
    }
  }
}
.drawer-card__stars {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  font-size: 14px;
  line-height: 1;
  letter-spacing: 1px;
  color: #e6a817;
  background: rgba(245, 197, 66, 0.22);
  font-weight: 600;
}
.drawer-card__meta {
  margin: 0 0 8px;
  font-size: 12px;
  color: #909399;
}
.drawer-card__title {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 650;
  line-height: 1.45;
  letter-spacing: 0.2px;
  color: #1f2d3d;
}
.drawer-card__body {
  margin: 0;
  padding: 12px 12px;
  border-radius: 8px;
  background: #f7f9fc;
  border: 1px solid #eef2f7;
  font-size: 14px;
  color: #4a5b70;
  line-height: 1.85;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  &.is-collapsed {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 8;
    overflow: hidden;
    white-space: pre-wrap;
  }
}
.drawer-card__more {
  margin-top: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #409eff;
  font-size: 13px;
  cursor: pointer;
  &:hover { opacity: 0.85; }
}
.drawer-empty { padding: 16px; color: #909399; }
.drawer-tip {
  margin: 0;
  padding: 12px 16px 20px;
  text-align: center;
  font-size: 12px;
  color: #909399;
}
.btn {
  border: 1 / @rem2 solid fade(@mainColor, 70%);
  background: transparent;
  color: #e8f3ff;
  border-radius: 4 / @rem2;
  padding: 6 / @rem2 14 / @rem2;
  font-size: 13 / @rem2;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 0 8 / @rem2 fade(@mainColor, 18%);
  transition: transform 0.12s ease, background 0.12s ease, box-shadow 0.12s ease,
    border-color 0.12s ease, filter 0.12s ease;
  &:hover {
    background: rgba(0, 120, 200, 0.22);
    border-color: fade(@mainColor, 95%);
    box-shadow: 0 0 12 / @rem2 fade(@mainColor, 40%);
  }
  &:active {
    transform: scale(0.94);
    background: rgba(4, 28, 70, 0.7);
    box-shadow: inset 0 2 / @rem2 6 / @rem2 rgba(0, 0, 0, 0.45);
    filter: brightness(0.88);
  }
}
.btn-branch-back {
  color: #041428;
  font-weight: 600;
  border-color: #5ad6ff;
  background: linear-gradient(180deg, #7ee7ff 0%, #2aa8e8 100%);
  box-shadow: 0 0 14 / @rem2 rgba(90, 214, 255, 0.4);
  &:hover {
    color: #041428;
    background: linear-gradient(180deg, #a8f0ff 0%, #3cbcf0 100%);
    border-color: #9eecff;
    box-shadow: 0 0 18 / @rem2 rgba(90, 214, 255, 0.55);
  }
}
.btn-unlock {
  color: #e8fbff;
  border-color: #5ad6ff;
  background: rgba(20, 140, 200, 0.35);
  box-shadow: 0 0 12 / @rem2 rgba(90, 214, 255, 0.4);
  &:hover {
    border-color: #9eecff;
    background: rgba(30, 160, 220, 0.45);
  }
}
.btn-danger {
  /* 设计稿取样：#930F18 / #9B121F 深红实心 + 红色外发光 */
  color: #fff;
  border: 1 / @rem2 solid #c62828;
  border-radius: 5 / @rem2;
  padding: 7 / @rem2 16 / @rem2;
  background:
    linear-gradient(180deg, rgba(255, 180, 180, 0.16) 0%, transparent 40%),
    linear-gradient(180deg, #b71c1c 0%, #9b121f 48%, #7a0c14 100%);
  box-shadow:
    0 0 8 / @rem2 rgba(200, 40, 50, 0.55),
    0 0 16 / @rem2 rgba(160, 20, 30, 0.35),
    inset 0 1 / @rem2 0 rgba(255, 160, 160, 0.22);
  text-shadow: 0 1 / @rem2 2 / @rem2 rgba(0, 0, 0, 0.4);
  &:hover {
    border-color: #e53935;
    background:
      linear-gradient(180deg, rgba(255, 200, 200, 0.2) 0%, transparent 40%),
      linear-gradient(180deg, #c62828 0%, #a5313a 48%, #8a1018 100%);
    box-shadow:
      0 0 12 / @rem2 rgba(220, 50, 60, 0.65),
      0 0 20 / @rem2 rgba(180, 30, 40, 0.4),
      inset 0 1 / @rem2 0 rgba(255, 180, 180, 0.28);
  }
  &:active {
    transform: scale(0.94);
    background: linear-gradient(180deg, #8a1018 0%, #5c0a10 100%);
    box-shadow:
      inset 0 2 / @rem2 6 / @rem2 rgba(0, 0, 0, 0.5),
      0 0 8 / @rem2 rgba(160, 20, 30, 0.4);
    filter: none;
  }
}
.btn-mic {
  width: 36 / @rem2;
  min-width: 36 / @rem2;
  padding: 6 / @rem2 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(180, 200, 220, 0.55);
  border-color: rgba(120, 150, 180, 0.35);
  background: rgba(8, 24, 48, 0.35);
  box-shadow: none;
  i {
    font-size: 16 / @rem2;
    line-height: 1;
  }
  &:hover {
    color: rgba(210, 225, 240, 0.85);
    background: rgba(0, 120, 200, 0.15);
    border-color: fade(@mainColor, 55%);
    box-shadow: none;
  }
  &.is-on {
    color: #e8fbff;
    border-color: #5ad6ff;
    background: rgba(20, 140, 200, 0.35);
    box-shadow: 0 0 12 / @rem2 rgba(90, 214, 255, 0.45);
  }
  &.is-flash {
    animation: voice-mic-flash 0.55s ease;
  }
}
@keyframes voice-mic-flash {
  0%,
  100% {
    filter: brightness(1);
    box-shadow: 0 0 12 / @rem2 rgba(90, 214, 255, 0.45);
  }
  40% {
    filter: brightness(1.45);
    box-shadow: 0 0 22 / @rem2 rgba(140, 240, 255, 0.85);
  }
}
.voice-tip {
  position: fixed;
  left: 50%;
  bottom: 48 / @rem2;
  transform: translateX(-50%);
  z-index: 5000;
  padding: 10 / @rem2 22 / @rem2;
  border-radius: 6 / @rem2;
  font-size: 15 / @rem2;
  letter-spacing: 1 / @rem2;
  color: #eaf7ff;
  background: rgba(6, 28, 56, 0.78);
  border: 1 / @rem2 solid rgba(90, 214, 255, 0.45);
  box-shadow: 0 4 / @rem2 18 / @rem2 rgba(0, 0, 0, 0.35);
  pointer-events: none;
  white-space: nowrap;
}
.voice-tip-enter-active,
.voice-tip-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.voice-tip-enter,
.voice-tip-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}
.btn-exit-inspect {
  position: relative;
  overflow: hidden;
  color: #e8f7ff;
  border: 1 / @rem2 solid rgba(0, 195, 255, 0.55);
  border-radius: 5 / @rem2;
  padding: 7 / @rem2 14 / @rem2;
  min-width: 88 / @rem2;
  background: rgba(0, 80, 140, 0.35);
  box-shadow: 0 0 10 / @rem2 rgba(0, 180, 255, 0.22);
  user-select: none;
  -webkit-user-select: none;
  &:hover {
    border-color: rgba(0, 220, 255, 0.85);
    background: rgba(0, 100, 160, 0.42);
  }
  &.pressing {
    transform: none;
    border-color: rgba(0, 230, 255, 0.95);
    box-shadow: 0 0 14 / @rem2 rgba(0, 200, 255, 0.45);
  }
  &:active {
    transform: none;
    filter: none;
  }
  &__fill {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: var(--press-progress, 0%);
    background: linear-gradient(
      90deg,
      rgba(0, 180, 255, 0.35),
      rgba(0, 220, 255, 0.55)
    );
    pointer-events: none;
    transition: width 0.05s linear;
  }
  &__label {
    position: relative;
    z-index: 1;
  }
}
.clock {
  font-size: 14 / @rem2;
  color: #e8f3ff;
  margin-left: 4 / @rem2;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5 / @rem2;
  white-space: nowrap;
  text-shadow: 0 0 8 / @rem2 fade(@mainColor, 30%);
}
.dash-main {
  flex: 1;
  min-height: 0;
  /* 上下边距已由 .dash-mid 统一，避免侧栏与内容高低不一致 */
  padding: 0 14 / @rem2;
}
.welcome-mask {
  position: absolute;
  inset: 0;
  z-index: 45;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #de2910;
  &__text {
    margin: 0;
    max-width: 72%;
    padding: 0 40 / @rem2;
    font-size: 72 / @rem2;
    font-weight: 700;
    letter-spacing: 0.12em;
    line-height: 1.35;
    text-align: center;
    color: #fff;
    text-shadow: 0 2 / @rem2 6 / @rem2 rgba(0, 0, 0, 0.25);
    word-break: break-word;
  }
}
.lock-mask {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(ellipse at 50% 42%, rgba(0, 120, 200, 0.22), transparent 55%),
    rgba(2, 10, 28, 0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.lock-box {
  position: relative;
  width: 400 / @rem2;
  padding: 36 / @rem2 32 / @rem2 28 / @rem2;
  text-align: center;
  border-radius: 12 / @rem2;
  border: 1 / @rem2 solid rgba(0, 195, 255, 0.45);
  background: linear-gradient(
    165deg,
    rgba(12, 40, 88, 0.92) 0%,
    rgba(6, 22, 52, 0.96) 100%
  );
  box-shadow:
    0 0 0 1 / @rem2 rgba(0, 195, 255, 0.12),
    0 18 / @rem2 48 / @rem2 rgba(0, 0, 0, 0.45),
    0 0 40 / @rem2 rgba(0, 180, 255, 0.18);
  overflow: hidden;
  &__glow {
    pointer-events: none;
    position: absolute;
    left: 10%;
    right: 10%;
    top: -40%;
    height: 70%;
    background: radial-gradient(
      ellipse at center,
      rgba(0, 195, 255, 0.28),
      transparent 70%
    );
  }
  &__icon {
    position: relative;
    width: 56 / @rem2;
    height: 56 / @rem2;
    margin: 0 auto 14 / @rem2;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 195, 255, 0.12);
    border: 1 / @rem2 solid rgba(0, 195, 255, 0.45);
    box-shadow: 0 0 18 / @rem2 rgba(0, 195, 255, 0.35);
    i {
      font-size: 26 / @rem2;
      color: #00e5ff;
    }
  }
  &__title {
    position: relative;
    margin: 0;
    font-size: 22 / @rem2;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #f0f7ff;
  }
  &__hint {
    position: relative;
    margin: 8 / @rem2 0 22 / @rem2;
    font-size: 13 / @rem2;
    color: rgba(180, 210, 240, 0.72);
  }
  &__field {
    position: relative;
    margin-bottom: 16 / @rem2;
    /deep/ .el-input__inner {
      height: 44 / @rem2;
      line-height: 44 / @rem2;
      border-radius: 6 / @rem2;
      border: 1 / @rem2 solid rgba(0, 195, 255, 0.35);
      background: rgba(4, 18, 42, 0.85);
      color: #e8f3ff;
      text-align: center;
      letter-spacing: 0.12em;
      &::placeholder {
        color: rgba(160, 190, 220, 0.45);
        letter-spacing: 0;
      }
      &:focus {
        border-color: #00c3ff;
        box-shadow: 0 0 0 2 / @rem2 rgba(0, 195, 255, 0.2);
      }
    }
    /deep/ .el-input__suffix {
      .el-input__icon {
        color: rgba(0, 195, 255, 0.75);
      }
    }
  }
  &__btn {
    position: relative;
    width: 100%;
    height: 44 / @rem2;
    border: 0;
    border-radius: 6 / @rem2;
    cursor: pointer;
    font-size: 15 / @rem2;
    font-weight: 600;
    letter-spacing: 0.12em;
    color: #041428;
    background: linear-gradient(90deg, #00c3ff 0%, #3de0c5 100%);
    box-shadow: 0 6 / @rem2 20 / @rem2 rgba(0, 195, 255, 0.35);
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
    &:hover {
      filter: brightness(1.06);
      box-shadow: 0 8 / @rem2 24 / @rem2 rgba(0, 220, 255, 0.45);
    }
    &:active {
      transform: translateY(1 / @rem2);
    }
  }
}
</style>

<style lang="less">
/* append-to-body 的抽屉不在 scoped 树内，需全局约束高度链，否则长文会撑破页面 */
.dash-drawer.el-drawer {
  display: flex;
  flex-direction: column;
  outline: none;
  background: #f5f7fb;
}
.dash-drawer .el-drawer__header {
  flex-shrink: 0;
  margin-bottom: 0;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #e6ebf2;
  background: #fff;
  span {
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }
}
.dash-drawer .el-drawer__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 12px 0 0;
  background: #f5f7fb;
}
.dash-drawer .drawer-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
